import { AdImageGallery } from '@/components/AdImageGallery';
import { AdSummaryItem } from '@/components/AdSummaryItem';
import { Button, LinkButton } from '@/components/Button';
import { Card } from '@/components/Card';
import { Flex, Grid } from '@/components/Grid';
import { Heading } from '@/components/Heading';
import { Illustration } from '@/components/Illustration';
import { Section } from '@/components/Section';
import { useCurrentUrl } from '@/hooks/useCurrentUrl';
import { useModal } from '@/hooks/useModal';
import { useAccount } from '@/resource/account';
import {
  canFulfillAd,
  closeAd,
  getAd,
  getRelevantAds,
  isFulfilledAd,
  isPublicAd,
  reopenAd,
  type AdItemType,
  type AdType,
} from '@/resource/ad';
import { generateAdTitle } from '@/util/generateAdTitle';
import { canUseNativeShare, nativeShare } from '@/util/nativeShare';
import { nl2br } from '@/util/nl2br';
import { staticProps } from '@/util/staticProps';
import { tryCatch } from '@/util/tryCatch';
import * as validators from '@/util/validators';
import { GetStaticPaths } from 'next';
import { NextSeo } from 'next-seo';
import { useQuery } from 'react-query';
import z from 'zod';
import { AdBackLink } from './AdBackLink';
import { CategoryList } from './CategoryList';
import { DeleteAdButton } from './DeleteAdButton';
import { FavoriteButton } from './FavoriteButton';
import { RelevantAds } from './RelevantAds';
import { ReportButton } from './ReportButton';
import { RespondToAd } from './RespondToAd';

type Props = {
  ad: AdItemType;
  relevantAds: AdItemType[];
};

export function AdDetail({ ad: initialData, relevantAds }: Props) {
  const modal = useModal();

  const { url } = useCurrentUrl();
  const { profile: ownProfile } = useAccount();
  const isOwnAd =
    ownProfile != null && initialData.profile.data.id === ownProfile.id;

  const {
    data: ad = initialData,
    refetch,
    isFetched,
  } = useQuery({
    queryKey: ['ad', initialData.id],
    queryFn: () => getAd({ id: initialData.id }),
    initialData,
    staleTime: 0,
    enabled: isOwnAd,
  });

  const categoryNames = ad.categories.data.map(({ name }) => name).join(', ');
  const isFulfilled = isFulfilledAd(ad);
  const isPublic = isPublicAd(ad);
  const adProfile = ad.profile.data;
  const label = ad.type === 'offer' ? 'Aanbod' : 'Vraag';
  const title = `${label} van ${adProfile.first_name}`;
  const description = generateAdTitle(ad);
  const images = ad.images?.data || [];
  const pageTitle = `${title} | ${categoryNames}`;

  const shareData = {
    text: description,
    title,
    url,
  };

  function share(onClose: () => void) {
    if (canUseNativeShare(shareData)) {
      return nativeShare(shareData);
    }

    modal.open(
      {
        type: 'share',
        props: {
          data: shareData,
          title: `Deel ${
            isOwnAd ? 'jouw' : ad.type === 'offer' ? 'dit' : 'deze'
          } ${label.toLowerCase()}`,
        },
      },
      onClose
    );
  }

  const canView1 = !ad.is_offline || (isOwnAd && isFetched);
  if (!canView1) {
    return <Offline ad={ad} />;
  }

  const canView2 = isPublic || (isOwnAd && isFetched);
  if (!canView2) {
    return <NotPublic ad={ad} />;
  }

  const seoImage = ad.image?.data.crop['16:9'][1200];

  return (
    <>
      <NextSeo
        title={pageTitle}
        description={description}
        openGraph={{
          title: pageTitle,
          description,
          images: seoImage
            ? [
                {
                  url: seoImage,
                  width: 1200,
                  height: 675,
                },
              ]
            : [],
        }}
      />

      <AdBackLink ad={ad} />

      <Section>
        <Grid columns={{ lg: [10] }} offsets={{ lg: 1 }}>
          {isOwnAd ? (
            <Flex justify="space-between">
              <Flex align="center" gap={2}>
                {!isFulfilled && (
                  <Button
                    key={ad.id}
                    theme="secondary"
                    icon="share"
                    iconOnly="mobile"
                    onClick={(e, refocus) => share(refocus)}
                  >
                    Deel {label.toLowerCase()}
                  </Button>
                )}
              </Flex>

              <Flex gap={2} align="center" justify="end">
                {!isFulfilled && (
                  <LinkButton
                    iconOnly="mobile"
                    icon="pen"
                    theme="secondary"
                    to={{ type: 'update_ad', ad }}
                  >
                    Bewerken
                  </LinkButton>
                )}

                <DeleteAdButton ad={ad} />
              </Flex>
            </Flex>
          ) : (
            <Flex gap={2} align="center" justify="end">
              <Button
                key={ad.id}
                theme="secondary"
                icon="share"
                iconOnly="mobile"
                onClick={(e, refocus) => share(refocus)}
              >
                Deel {label.toLowerCase()}
              </Button>
              <FavoriteButton ad={ad} />
            </Flex>
          )}
        </Grid>

        <Grid columns={{ lg: [10, 1] }} offsets={{ lg: 1 }}>
          <Heading align="center" balance>
            {ad.title}
          </Heading>
        </Grid>

        <Grid
          columns={{ md: [5, 7], lg: [4, 6] }}
          offsets={{ lg: 1 }}
          gap={{ sm: 4, md: 3, lg: 4 }}
        >
          <Flex dir="column" gap={4}>
            <AdSummaryItem item={ad} />
          </Flex>

          <Flex dir="column" gap={4}>
            <AdDescription ad={ad} />

            {ad.type === 'offer' && <CategoryList ad={ad} />}

            {images.length > 1 && <AdImageGallery ad={ad} images={images} />}

            {isOwnAd && canFulfillAd(ad) && (
              <>
                {isFulfilled ? (
                  <Flex dir="column" gap={2}>
                    <p>
                      Je {label.toLowerCase()} is ingevuld. Wil je hem opnieuw
                      beschikbaar maken, dan kun je hem hieronder weer
                      herplaatsen.
                    </p>
                    <Button
                      theme="secondary"
                      icon="upload"
                      onClick={() => reopenAd(ad).then(() => refetch())}
                    >
                      {label} herplaatsen
                    </Button>
                  </Flex>
                ) : (
                  <Flex dir="column" gap={2}>
                    <p>
                      Heb je iemand gevonden? Dan kan je hieronder je{' '}
                      {label.toLowerCase()} afsluiten.
                    </p>
                    <Button
                      theme="secondary"
                      icon="check_thin"
                      onClick={() => closeAd(ad).then(() => refetch())}
                    >
                      Ik heb iemand gevonden!
                    </Button>
                  </Flex>
                )}
              </>
            )}

            {isPublic && !isOwnAd && <RespondToAd ad={ad} />}
          </Flex>
        </Grid>

        {!isOwnAd && ownProfile != null && !ownProfile.deactivated && (
          <Grid
            columns={{ md: [5, 7], lg: [4, 6] }}
            offsets={{ lg: 1 }}
            gap={{ sm: 4, md: 3, lg: 4 }}
          >
            <Flex dir="column" gap={4}>
              <ReportButton ad={ad} />
            </Flex>
          </Grid>
        )}
      </Section>

      {relevantAds.length > 0 && (
        <RelevantAds ads={relevantAds} adType={ad.type} />
      )}
    </>
  );
}

function AdDescription({ ad }: { ad: AdItemType }) {
  if (ad.type === 'offer' && ad.use_generated_title) {
    return <p>{generateAdTitle(ad, [], 10)}</p>;
  }

  return (
    <p
      style={
        ad.description.split(/\s+/g).some((word) => word.length > 35)
          ? { wordBreak: 'break-word' }
          : undefined
      }
      dangerouslySetInnerHTML={{ __html: nl2br(ad.description) }}
    ></p>
  );
}

function Unavailable({ message }: { message: string }) {
  return (
    <>
      <NextSeo title={message} description={message} />
      <Section>
        <Grid columns={{ md: [8], lg: [6] }} offsets={{ md: 2, lg: 3 }}>
          <Heading balance align="center">
            {message}.
          </Heading>
          <Card>
            <Illustration aspectRatio="16-9" illustration="vraag_hulp" />
          </Card>
        </Grid>
      </Section>
    </>
  );
}

function Offline({ ad }: { ad: AdItemType }) {
  const pronoun = ad.type === 'offer' ? 'Dit' : 'Deze';
  const label = ad.type === 'offer' ? 'aanbod' : 'vraag';
  const message = `${pronoun} ${label} is offline`;
  return <Unavailable message={message} />;
}

function NotPublic({ ad }: { ad: AdItemType }) {
  const pronoun = ad.type === 'offer' ? 'Dit' : 'Deze';
  const label = ad.type === 'offer' ? 'aanbod' : 'vraag';
  const message = `${pronoun} ${label} is op dit moment niet beschikbaar`;
  return <Unavailable message={message} />;
}

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking',
  };
};

export const getStaticPropsFor = (adType: AdType) =>
  staticProps
    .params(
      z.object({
        params: z.tuple([
          validators.slug({ min: 1, max: 128 }),
          validators.numeric({ min: 1, max: 12 }),
        ]),
      })
    )
    .resolve<Props>(async ({ params: [slug, id] }) => {
      const ad = await tryCatch(getAd({ id }));

      if (
        !ad?.profile ||
        ad.type !== adType ||
        ad.profile.data.slug !== slug ||
        ad.profile.data.banned
      ) {
        return {
          notFound: true,
          revalidate: 10,
        };
      }

      return {
        props: {
          ad,
          relevantAds: await getRelevantAds({ ad, limit: 5 }),
        },
        revalidate: 30,
      };
    });
